/**
 * Board Helper Functions
 *
 * Provides utility functions for board-related logic, such as validating moves,
 * checking island connections, and parsing board states.
 *
 * Integration:
 * - Used by: useBoard hook, Board components, and IslandPlacementPhase.
 * - Depends on: board.types.ts, monster.types.ts, game.types.ts.
 */

import type { Position, BoardState } from '../types/game.types';
import type { SquareData } from '../types/board.types';
import type { PlayerLetter } from '../types/game.types';
import { BOARD_ROWS, BOARD_COLS, PLAYER_A_MAX_SQUARE, PLAYER_B_MIN_SQUARE } from './constants.ts';

// ============================================================================
// Board Parsing Helpers
// ============================================================================

/**
 * Parse board state from backend format to frontend square array
 * Converts backend `boardState.tiles` to `SquareData[]` for all squares
 * @param boardState - Backend board state
 * @returns Array of square data for all board squares
 */
export const parseBoardState = (boardState: BoardState): SquareData[] => {
    const squares: SquareData[] = [];

    for (let row = 0; row < boardState.rows; row++) {
        for (let col = 0; col < boardState.columns; col++) {
            const key = `${row}:${col}`;
            const tile = boardState.tiles[key];

            squares.push({
                row,
                col,
                type: tile?.type || 'blank',
                player: tile?.player || null,
                value: tile?.value || null,
            });
        }
    }

    return squares;
};

// ============================================================================
// Island Placement Helpers
// ============================================================================

/**
 * Check if islands are connected (all islands can reach each other via adjacent squares)
 * Uses depth-first search to verify connectivity
 * @param islands - Array of island positions
 * @returns True if all islands are connected
 */
export const areIslandsConnected = (islands: Position[]): boolean => {
    if (islands.length === 0) return true; // Empty array is trivially connected

    const visited = new Set<string>();
    const [start] = islands;
    const stack: Position[] = [start];

    const directions: Position[] = [
        { row: 1, col: 0 },  // Down
        { row: -1, col: 0 }, // Up
        { row: 0, col: 1 },  // Right
        { row: 0, col: -1 }, // Left
    ];

    // Helper function to check if a coordinate is in the islands array
    const isValid = (pos: Position): boolean => {
        return islands.some(island => island.row === pos.row && island.col === pos.col);
    };

    // Helper function to create position key
    const getKey = (pos: Position): string => `${pos.row},${pos.col}`;

    // Depth-First Search (DFS) to explore the islands
    while (stack.length > 0) {
        const current = stack.pop()!;
        const key = getKey(current);
        visited.add(key);

        for (const dir of directions) {
            const newPos: Position = {
                row: current.row + dir.row,
                col: current.col + dir.col,
            };

            const newKey = getKey(newPos);
            if (isValid(newPos) && !visited.has(newKey)) {
                stack.push(newPos);
            }
        }
    }

    // Check if all islands were visited
    return islands.length === visited.size;
};

/**
 * Validate island placement for a player
 * Checks:
 * - Correct number of islands
 * - Islands are within player's half
 * - Islands are connected
 * @param islands - Array of island positions
 * @param playerLetter - Player letter ('A' or 'B')
 * @returns Validation result with error message if invalid
 */
export const validateIslandPlacement = (
    islands: Position[],
    playerLetter: PlayerLetter
): { valid: boolean; error?: string } => {
    // Check number of islands
    if (islands.length !== 5) {
        return { valid: false, error: 'Must place exactly 5 islands' };
    }

    // Check if islands are in correct half
    for (const island of islands) {
        const squareNumber = island.row * BOARD_COLS + island.col;

        if (playerLetter === 'A' && squareNumber > PLAYER_A_MAX_SQUARE) {
            return { valid: false, error: 'Islands must be in the top half (rows 0-9)' };
        }

        if (playerLetter === 'B' && squareNumber <= PLAYER_B_MIN_SQUARE - 1) {
            return { valid: false, error: 'Islands must be in the bottom half (rows 10-19)' };
        }
    }

    // Check if islands are connected
    if (!areIslandsConnected(islands)) {
        return { valid: false, error: 'All islands must be connected' };
    }

    return { valid: true };
};

/**
 * Convert position to square number (0-199)
 * @param position - Position {row, col}
 * @returns Square number
 */
export const positionToSquareNumber = (position: Position): number => {
    return position.row * BOARD_COLS + position.col;
};

/**
 * Check if a square is in player's half
 * @param position - Position to check
 * @param playerLetter - Player letter ('A' or 'B')
 * @returns True if square is in player's half
 */
export const isInPlayerHalf = (position: Position, playerLetter: PlayerLetter): boolean => {
    const squareNumber = positionToSquareNumber(position);

    if (playerLetter === 'A') {
        return squareNumber <= PLAYER_A_MAX_SQUARE;
    } else {
        return squareNumber >= PLAYER_B_MIN_SQUARE;
    }
};

// ============================================================================
// Position Helpers
// ============================================================================

/**
 * Check if a position is within board bounds
 * @param position - Position to check
 * @returns True if position is valid
 */
export const isValidPosition = (position: Position): boolean => {
    return (
        position.row >= 0 &&
        position.row < BOARD_ROWS &&
        position.col >= 0 &&
        position.col < BOARD_COLS
    );
};
